import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { withCtxAbortOnStatusCode } from '@bonnet/next/ctx-middleware/with-ctx-abort-on-status-code';

import { pageNames } from '@atc/bonnet-paths';

import ctxPresetFindCar from '@/context-middleware/ctxPresetFindCar';
import addVehicleDetailsPageData from '@/context-middleware/vdp/addVehicleDetailsPageData';
import dispatchVehicleDetailsPageData from '@/context-middleware/vdp/dispatchVehicleDetailsPageData';
import withInventoryPageRedirect from '@/context-middleware/vdp/redirectToInventoryPage';
import withVDPExpired from '@/context-middleware/vdp/withVDPExpired';
import withCustomLeadRouting from '@/context-middleware/withCustomLeadRouting';
import withDataIslandDispatcher from '@/context-middleware/withDataIslandDispatcher';
import withInterestRates from '@/context-middleware/withInterestRates';
import withWalletConfigs from '@/context-middleware/withWalletConfigs';

import srpNewSearchDuck from '@/ducks/srp/srpNewSearchDuck';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck';
import srpReuseActiveStateDuck from '@/ducks/srp/srpReuseActiveStateDuck';

const { VEHICLE_DETAILS } = pageNames;

/**
 * Executes common VDP (Vehicle Details Page) initialization logic for getInitialProps.
 *
 * This function handles the standard initialization sequence for all VDP pages, including:
 * - Managing SRP (Search Results Page) state persistence
 * - Running core middleware for data fetching and processing
 * - Handling redirects, expired listings, and error states
 * - Setting up interest rates, wallet configs, and custom lead routing
 *
 * @param {import('next').NextPageContext & { store: import('redux').Store, data: Object, withModuleCtxMiddleware: Function }} ctx - Next.js page context object with Redux store and custom properties
 * @param {Array<Function>} [additionalMiddleware=[]] - Optional array of additional middleware functions to execute after the core VDP middleware chain. Useful for page-specific middleware like vehicle history reports.
 *
 * @returns {Promise<void>} Resolves when all middleware has been executed. Check ctx.statusCode after execution to determine if an error occurred.
 *
 * @example
 * // Basic usage without additional middleware
 * await initializeVdpPageContext(ctx);
 *
 * @example
 * // With additional page-specific middleware
 * await initializeVdpPageContext(ctx, [withVehicleHistoryReport()]);
 */
export async function initializeVdpPageContext(ctx, additionalMiddleware = []) {

    if (srpResultsDuck.selectors.hasActiveResults(ctx.store.getState())) {
        ctx.store.dispatch(srpReuseActiveStateDuck.creators.setTrue());
    }

    ctx.store.dispatch(srpNewSearchDuck.creators.setFalse());

    await withCtxMiddleware([
        withInventoryPageRedirect(),
        ctxPresetFindCar({
            pageName: VEHICLE_DETAILS,
            preFetchCtxMiddleware: [
                ctx.withModuleCtxMiddleware({
                    batchDispatch: false,
                    fetchCompositeJSON: false,
                }),
            ],
            fetcher: addVehicleDetailsPageData,
            dispatcher: dispatchVehicleDetailsPageData,
        }),
        withVDPExpired(),
        withCtxAbortOnStatusCode(),
        withDataIslandDispatcher(),
        withInterestRates(),
        withWalletConfigs(),
        withCustomLeadRouting(),
        ...additionalMiddleware,
    ], ctx);
}
